import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { deregisterFcmToken } from '../../services/fcmService';

const apiUrl = process.env.REACT_APP_API_URL;
const chatApiUrl = process.env.REACT_APP_CHAT_API_URL || 'http://68.178.162.134:5001/api/v1';

/**
 * Login to chat server to obtain chat token
 * Called after main Laravel login with same credentials
 */
const loginToChatServer = async (email, password) => {
  try {
    const response = await axios.post(`${chatApiUrl}/auth/login`, {
      email,
      password
    });
    return response.data.token;
  } catch (error) {
    console.warn('Chat server login failed:', error.message);
    // Return null instead of throwing - chat is not critical for app function
    return null;
  }
};

export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      // 1. Login to main Laravel API
      const response = await axios.post(`${apiUrl}api/authenticate?from=web`, { username, password });
      
      // 2. Login to chat server in parallel (non-blocking)
      let chatToken = null;
      try {
        chatToken = await loginToChatServer(username, password);
      } catch (chatError) {
        console.warn('Chat login failed, continuing without chat:', chatError);
      }
      
      // 3. Return combined data
      return {
        ...response.data,
        chatToken
      };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: null,
    role: null,
    menus: [],
    selectedMenu: {},
    selectedSubMenu: {},
    current_academic_id: null,
    academic_years: [],
    encryptedMenus:null,
    institute: null,
    loading: false,
    uiMessage: {
        error: null,
    },
    // Chat-related state
    chatToken: null,
    chatConnected: false,
  },
  reducers: {
    logout: (state) => {
      // Deregister FCM token (async, fire-and-forget)
      deregisterFcmToken().catch(() => {});
      
      state.user = null;
      state.token = null;
      state.role = null;
      state.menus = [];
      state.selectedMenu = {};
      state.selectedSubMenu = {};
      state.loading = false;
      state.uiMessage.error = null;
      state.encryptedMenus = null;
      state.institute = null;
      // Clear chat state on logout
      state.chatToken = null;
      state.chatConnected = false;
    },
    setSelectedMenu: (state, {payload}) => {
      state.selectedMenu = payload;
    },
    setSelectedSubMenu: (state, {payload}) => {
      state.selectedSubMenu = payload;
    },
    setCurrentAcademicYear : (state, { payload }) => {
      state.current_academic_id = payload;
    },
    setBrandConfig: (state, { payload }) => {
      state.institute = payload;
    },
    updateUser: (state, { payload }) => {
      state.user = payload.user || payload;
      if (payload.institute) {
        state.institute = payload.institute;
      }
      if (payload.role) {
        state.role = payload.role;
      }
    },
    // Chat token management
    setChatToken: (state, { payload }) => {
      state.chatToken = payload;
    },
    setChatConnectionStatus: (state, { payload }) => {
      state.chatConnected = payload;
    },
    // Refresh chat token (called when token expires)
    refreshChatToken: (state, { payload }) => {
      state.chatToken = payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.uiMessage.error = null;
      })
      .addCase(login.fulfilled, (state, { payload }) => {
        state.loading = false;
        const {access_token, user, menus, academic_years = [],encryptedMenus, institute, chatToken} = payload;
        state.token = access_token;
        state.user = user;
        state.role = user.role;
        state.selectedMenu = menus[0];
        state.menus = menus;
        state.current_academic_id = academic_years.filter((year) => year.is_current)[0]?.academic_id || null;
        state.selectedSubMenu = menus[0]?.sub_menus[0] || {};
        state.academic_years = academic_years;
        state.encryptedMenus = encryptedMenus;
        state.institute = institute;
        // Store chat token from chat server login
        state.chatToken = chatToken || null;
      })
      .addCase(login.rejected, (state, { payload }) => {
        state.loading = false;
        state.uiMessage.error = payload || 'An error occurred';
      });
  },
});

export const { 
  setToken, 
  setUser, 
  setRoles, 
  logout, 
  setSelectedMenu, 
  setSelectedSubMenu, 
  setCurrentAcademicYear, 
  setBrandConfig, 
  updateUser,
  setChatToken,
  setChatConnectionStatus,
  refreshChatToken
} = authSlice.actions;
export default authSlice.reducer;
